import { pan } from './viewportState.js';
import type { EdgeRef, PendingConnection } from '../editor/connectionMutations.js';
import { edgeKey, type EdgeVisualStateController } from './edgeVisualState.js';

interface Point {
  x: number;
  y: number;
}

interface EdgeDetachDeps {
  getEdges: () => EdgeRef[];
  getDotWorldPos: (nodeId: string, portIdx: number, isOut: boolean) => Point;
  visualState: EdgeVisualStateController;
  disconnectEdgeToPending: (
    edge: EdgeRef,
    clickPos: Point,
    getDotWorldPos: (nodeId: string, portIdx: number, isOut: boolean) => Point,
  ) => PendingConnection | null;
  setPendingConn: (pendingConn: PendingConnection) => void;
  refreshRenderedConnections: () => void;
  renderGraph: () => void;
  scheduleRender: () => void;
  forceRender: () => void;
}

function eventToWorld(ev: MouseEvent): Point {
  const graphArea = document.getElementById('graph-canvas-area');
  if (!graphArea) return { x: 0, y: 0 };
  const rect = graphArea.getBoundingClientRect();
  return {
    x: (ev.clientX - rect.left - pan.x) / pan.scale,
    y: (ev.clientY - rect.top - pan.y) / pan.scale
  };
}

export function createEdgeDetachController(deps: EdgeDetachDeps) {
  let detachPreviewDot: HTMLElement | null = null;

  const shouldSuppressDetach = (ev?: MouseEvent): boolean => deps.visualState.isCuttingEdges() || !!ev?.ctrlKey;

  const clearDetachPreview = (): void => {
    if (detachPreviewDot) {
      detachPreviewDot.classList.remove('detach-target');
      detachPreviewDot = null;
    }
    deps.visualState.setDetachPreviewEdgeKey(null);
    deps.refreshRenderedConnections();
  };

  const setDetachPreview = (nodeId: string, portIdx: number, io: 'in' | 'out'): void => {
    const nextDot = document.querySelector(`[data-node="${nodeId}"][data-port="${portIdx}"][data-io="${io}"]`) as HTMLElement | null;
    if (detachPreviewDot === nextDot) return;
    clearDetachPreview();
    if (!nextDot) return;
    nextDot.classList.add('detach-target');
    detachPreviewDot = nextDot;
  };

  const setDetachPreviewEdge = (fromNode: string, fromPort: number, toNode: string, toPort: number): void => {
    deps.visualState.setDetachPreviewEdgeKey(edgeKey(fromNode, fromPort, toNode, toPort));
    deps.refreshRenderedConnections();
  };

  const pickSocketDetachEdge = (
    io: 'in' | 'out',
    nodeId: string,
    portIdx: number,
    ev?: MouseEvent
  ): EdgeRef | null => {
    const edges = deps.getEdges();
    if (io === 'in') {
      return edges.find(edge => edge.toNode === nodeId && edge.toPort === portIdx) || null;
    }
    const outEdges = edges.filter(edge => edge.fromNode === nodeId && edge.fromPort === portIdx);
    if (outEdges.length === 0) return null;
    if (!ev || outEdges.length === 1) return outEdges[0];
    const pointerPos = eventToWorld(ev);
    return outEdges.reduce((best, candidate) => {
      const bestPos = deps.getDotWorldPos(best.toNode, best.toPort, false);
      const candPos = deps.getDotWorldPos(candidate.toNode, candidate.toPort, false);
      const bestDist = Math.hypot(pointerPos.x - bestPos.x, pointerPos.y - bestPos.y);
      const candDist = Math.hypot(pointerPos.x - candPos.x, pointerPos.y - candPos.y);
      return candDist < bestDist ? candidate : best;
    });
  };

  const previewSocketDetach = (io: 'in' | 'out', nodeId: string, portIdx: number, ev?: MouseEvent): void => {
    if (shouldSuppressDetach(ev)) {
      clearDetachPreview();
      return;
    }
    const edge = pickSocketDetachEdge(io, nodeId, portIdx, ev);
    if (!edge) {
      clearDetachPreview();
      return;
    }
    if (io === 'in') setDetachPreview(nodeId, portIdx, 'in');
    else setDetachPreview(nodeId, portIdx, 'out');
    setDetachPreviewEdge(edge.fromNode, edge.fromPort, edge.toNode, edge.toPort);
  };

  const previewDetachTarget = (fromNode: string, fromPort: number, toNode: string, toPort: number, ev: MouseEvent): void => {
    if (shouldSuppressDetach(ev)) {
      clearDetachPreview();
      return;
    }
    const clickPos = eventToWorld(ev);
    const fromPos = deps.getDotWorldPos(fromNode, fromPort, true);
    const toPos = deps.getDotWorldPos(toNode, toPort, false);
    const distToFrom = Math.hypot(clickPos.x - fromPos.x, clickPos.y - fromPos.y);
    const distToTo = Math.hypot(clickPos.x - toPos.x, clickPos.y - toPos.y);
    const detachInputSide = distToTo <= distToFrom;
    if (detachInputSide) setDetachPreview(toNode, toPort, 'in');
    else setDetachPreview(fromNode, fromPort, 'out');
    setDetachPreviewEdge(fromNode, fromPort, toNode, toPort);
  };

  const disconnectEdgeSocket = (fromNode: string, fromPort: number, toNode: string, toPort: number, ev: MouseEvent): void => {
    if (shouldSuppressDetach(ev)) {
      clearDetachPreview();
      return;
    }
    const clickPos = eventToWorld(ev);
    const pending = deps.disconnectEdgeToPending({ fromNode, fromPort, toNode, toPort }, clickPos, deps.getDotWorldPos);
    if (!pending) return;

    deps.setPendingConn({
      ...pending,
      suppressMouseUp: true,
      ignoreNextClick: true,
      startScreen: { x: ev.clientX, y: ev.clientY }
    });
    deps.scheduleRender();
    deps.renderGraph();
    deps.forceRender();
  };

  return {
    clearDetachPreview,
    previewSocketDetach,
    previewDetachTarget,
    disconnectEdgeSocket,
    pickSocketDetachEdge
  };
}
